// Central source of truth for every Cognito auth URL the dashboard builds.
//
// The redirect nightmare we used to have came from baking dashboard-origin
// URLs into the bundle at build time (CRA inlines `process.env.REACT_APP_*`
// during `npm run build`). The login authorize URL and the token-exchange
// `redirect_uri` lived in two separate env vars that had to be kept in sync
// across local / staging / prod, and the logout URL was hardcoded to prod.
//
// Instead, we derive `redirect_uri` and `logout_uri` from
// `window.location.origin` at runtime, so a single build works correctly on
// whatever origin it is actually served from. The only values that genuinely
// differ from the dashboard origin are the Cognito hosted-UI domain and the
// app client id (both tied to the user pool), which stay as env vars.
//
// If those two vars are missing we fall back to the legacy build-time vars so
// existing deployments keep working — but the logout redirect is always
// origin-derived now, fixing the cross-environment bounce regardless.

const COGNITO_DOMAIN = (process.env.REACT_APP_COGNITO_APP_URL || '').replace(/\/+$/, '')
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || ''
// `openid` on its own returns every client-readable attribute from userInfo
// (name, custom:organisation, custom:admin…). `aws.cognito.signin.user.admin`
// additionally lets the access token call self-service user-pool APIs — the
// backend uses it for the profile page's change-password flow (Cognito's
// ChangePassword verifies the current password itself). It is not an OIDC
// attribute scope, so it does not narrow the userInfo claims the way
// `profile`/`email`/`phone` would.
const SCOPE = 'openid aws.cognito.signin.user.admin'

// Runtime mode is only possible when we know the hosted-UI domain and client id.
const runtimeMode = Boolean(COGNITO_DOMAIN && CLIENT_ID)

// Where Cognito sends the user back after a successful login. Must match the
// `redirect_uri` used both in the authorize request (getLoginUrl) and the
// subsequent token exchange — keeping them in one place guarantees that.
export const getRedirectUri = (): string =>
    runtimeMode ? `${window.location.origin}/auth` : `${process.env.REACT_APP_COGNITO_REDIRECT_URI}`

// Full Cognito hosted-UI login URL.
export const getLoginUrl = (): string => {
    if (!runtimeMode) {
        return `https://${process.env.REACT_APP_DASHBOARD_LOGIN}`
    }
    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: `${window.location.origin}/auth`,
        response_type: 'code',
        scope: SCOPE,
    })
    return `${COGNITO_DOMAIN}/login?${params.toString()}`
}

// Full Cognito hosted-UI logout URL. The post-logout redirect is always
// derived from the current origin so logging out on staging/local no longer
// bounces the user to production.
export const getLogoutUrl = (): string => {
    const logoutRedirect = `${window.location.origin}/login`
    if (!runtimeMode) {
        return `${process.env.REACT_APP_COGNITO_APP_URL}/logout?client_id=${process.env.REACT_APP_COGNITO_CLIENT_ID}&logout_uri=${logoutRedirect}`
    }
    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        logout_uri: logoutRedirect,
    })
    return `${COGNITO_DOMAIN}/logout?${params.toString()}`
}
